import { Row, message } from 'antd';
import { useState, useMemo, useEffect } from 'react';
import { map as _map, reduce as _reduce } from 'lodash';
import { useParams } from 'react-router';
import API from '../../../services/API';
import LoaderComponent from '../../Loader';
import LoanDetails from './Loans/LoanDetails';
import LoanActions from './Loans/LoanActions';
import { PAYMENT_PRODUCT_TYPE } from '../../../constants/AppConstants';
import AutoPayAction from './Actions/AutoPayAction';
import { isSupportAgent2 } from '../../../helpers/genericHelper';
import { getAutoPayStatus } from '../../../helpers/parsingHelper';

function SaveAndBuildCredit({
  buildCredits = [], loading, refreshList, stripeCards, userSummary, loanDetails, paymentAttempts, autoPayStatus
 }) {
  const [localBuildCredits, setLocalBuildCredits] = useState(buildCredits);
  const [localLoading, setLocalLoading] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [currentLoanId, setCurrentLoanId] = useState(null);
  const [refundModalVisible, seRrefundModalVisible] = useState(false);
  const [currentData, setCurrentData] = useState(null);
  const [modalLoading, showModalLoading] = useState(false);
  const [showChangeDueDateModal, setShowChangeDueDateModal] = useState(false);
  const [updatedDueDate, setUpdatedDueDate] = useState(null);
  const [chargeSilaModalVisible, setChargeSilaModalVisible] = useState(false);
  const [showToggleAutoRenewModal, setToggleAutoRenewModal] = useState(false);
  const [updatedAutoRenew, setUpdatedAutoRenew] = useState(null);
  const [autoPayStatusBoolean, setAutoPayStatusBoolean] = useState(true);

  const autoPayStatusData = getAutoPayStatus(autoPayStatus, PAYMENT_PRODUCT_TYPE.LOAN);
  
  let { userId } = useParams();

  useEffect(() => {
    if (!dataFetched && (buildCredits.length === 0 || !buildCredits)) {
      fetchSavingsBuilderData();
    }
  }, [userId, buildCredits.length, dataFetched]);

  const fetchSavingsBuilderData = async () => {
    try {
      setLocalLoading(true);
      const response = await API.getSavingsBuilder(userId);
      setLocalBuildCredits(response?.savingBuilders || []);
      setDataFetched(true);
    } catch (error) {
      console.error('Error fetching savings builder data:', error);
      message.error('Failed to load savings builder data');
    } finally {
      setLocalLoading(false);
    }
  };

  const closeAccount = async () => {
    try {
      showModalLoading(true);
      await API.closeSaveAndBuild(userId, currentLoanId);
      message.success('Successfully closed the Account.');
    } catch (ex) {
      message.error(ex?.response?.data?.error_message);
    } finally {
      showModalLoading(false);
      setModalVisible(false);
      refreshList();
    }
  }
  const closeModal = () => {
    setModalVisible(false);
  }


  const showCloseAccountModal = (data) => {
    setCurrentLoanId(data?.sbLoanEntry?.id)
    setModalVisible(true);
  }

  const toggleAutoRenew = async () => {
    try {
      showModalLoading(true);
      const willAutoRenew = Boolean(updatedAutoRenew);
      await API.toggleAutoRenewSB(userId, willAutoRenew);
      message.success(`Successfully updated Auto Renew.`);
    } catch (ex) {
      message.error(ex?.response?.data?.error_message);
    } finally {
      showModalLoading(false);
      setToggleAutoRenewModal(false);
      refreshList();
    }
  }

  const onToggleAutoRenew = (e) => {
    const value = JSON.parse(e.target.value);
    setUpdatedAutoRenew(value);
  }

  const openToggleAutoRenewModal = (data) => {
    setToggleAutoRenewModal(true);
  }

  const closeToggleAutoRenewModal = () => {
    setToggleAutoRenewModal(false);
    setUpdatedAutoRenew(null);
  }


  const openChangeDueDateModal = (data) => {
    setCurrentLoanId(data?.sbLoanEntry?.id)
    setShowChangeDueDateModal(true);
  }

  const closeDueDateModal = () => {
    setShowChangeDueDateModal(false);
    setUpdatedDueDate(null);
  }

  const changeDueDate = async () => {
    const loanId = currentLoanId;
    if (loanId) {
      try {
        showModalLoading(true);
        await API.changeDueDate(userId, updatedDueDate, true, loanId);
        message.success('Successfully changed the Due Date.');
      } catch (e) {
        message.error('Unable to change the Due Date.');
      } finally {
        setShowChangeDueDateModal(false);
        showModalLoading(false);
        refreshList();
      }
    }
  }

  const onDueDateValueChange = (val) => {
    setUpdatedDueDate(val);
  }


  const showChargeSilaModal = (data) => {
    setCurrentLoanId(data?.sbLoanEntry?.id);
    setChargeSilaModalVisible(true);
  }

  const closeChargeSilaModal = () => {
    setChargeSilaModalVisible(false)
  }


  const showRefundModalVisible = (data) => {
    setCurrentData(data);
    seRrefundModalVisible(true);
  }

  const closeRefundModal = () => {
    setCurrentData(null);
    seRrefundModalVisible(false);
  }

  const sortedBuildCredits = useMemo(() => {
    // Use localBuildCredits if we fetched data, otherwise use buildCredits from props
    const creditsToUse = dataFetched ? localBuildCredits : buildCredits;
    if (!creditsToUse || creditsToUse.length === 0) return [];

    return creditsToUse.sort((a, b) => {
      const aTs = new Date(a.sbLoanEntry?.createTs);
      const bTs = new Date(b.sbLoanEntry?.createTs);
      return bTs - aTs;
    });
  }, [buildCredits, localBuildCredits, dataFetched, loanDetails]);

  return (
    <>
      {(loading || localLoading) ? (
        <LoaderComponent />
      ) : (
        <>
          {sortedBuildCredits.length > 0 ? sortedBuildCredits.map((data) =>
            <LoanDetails
              data={data}
              loanData={loanDetails}
              userSummary={userSummary}
              key={data?.sbLoanEntry?.id}
              openChangeDueDateModal={openChangeDueDateModal}
              showRefundModalVisible={showRefundModalVisible}
              showCloseAccountModal={showCloseAccountModal}
              openToggleAutoRenewModal={openToggleAutoRenewModal}
              showChargeSilaModal={showChargeSilaModal}
              paymentAttempts={paymentAttempts}
              autoPayStatusEnabled={!autoPayStatusData.isDisabled}
              autoPayStatusData={autoPayStatusData}
            />
          ) : (
            <Row className="no-data-card">No Data Found</Row>
          )}
          { /** Auto pay action for savings builder */}
          {isSupportAgent2() && <AutoPayAction
          autoPayStatusEnabled={!(autoPayStatus?.isLoanAutoPayDisabled == true)} 
          refreshList={refreshList} 
          type={PAYMENT_PRODUCT_TYPE.SAVINGS_BUILDER} 
          userId={userId} 
          originalAutoPayDay={getAutoPayStatus(autoPayStatus, PAYMENT_PRODUCT_TYPE.LOAN)?.autoPayDay}
          />}
          <LoanActions
            showChangeDueDateModal={showChangeDueDateModal}
            closeDueDateModal={closeDueDateModal}
            closeChargeSilaModal={closeChargeSilaModal}
            closeRefundModal={closeRefundModal}
            closeModal={closeModal}
            modalVisible={modalVisible}
            currentLoanId={currentLoanId}
            modalLoading={modalLoading}
            closeToggleAutoRenewModal={closeToggleAutoRenewModal}
            updatedAutoRenew={updatedAutoRenew}
            onToggleAutoRenew={onToggleAutoRenew}
            toggleAutoRenew={toggleAutoRenew}
            updatedDueDate={updatedDueDate}
            onDueDateValueChange={onDueDateValueChange}
            refreshList={refreshList}
            closeAccount={closeAccount}
            changeDueDate={changeDueDate}
            currentData={currentData}
            userId={userId}
            stripeCards={stripeCards}
            chargeSilaModalVisible={chargeSilaModalVisible}
            refundModalVisible={refundModalVisible}
            />
        </>
      )}
      
    </>
  );
}

export default SaveAndBuildCredit;
